import React, { useContext, useMemo } from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { StyledBox } from './RecipientsBlock.styles';
import { usePostOfficeBottomSheetState } from '@apps/admin/postOffice/PostOffice.bottomSheet';
import { PeopleContext } from '@shared/context/people';
import { GuestPickerButton } from '@shared/components/ContactPicker';
import { useScrollPosition } from '@shared/utils/hooks/useScrollPosition';
import { ReactComponent as AddGuestsSvg } from '@assets/icons/add-guests.svg';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';

const RecipientsBlock: React.FC = () => {
  const { tq } = useTranslation('emailsAndEcards');
  const { step } = usePostOfficeBottomSheetState();
  const { selectedPeople } = useContext(PeopleContext);
  const scrollPosition = useScrollPosition();

  const isHighlighted = ['send-by-email', 'recipients'].includes(step);
  const relation = Math.min(1, scrollPosition / 50);
  const opacity = 1 - relation;

  const text = useMemo(() => {
    if (!selectedPeople.length) {
      return tq`No recipients selected`;
    }

    const firstGuestName = selectedPeople[0].full_name || '';
    if (selectedPeople.length === 1) {
      return firstGuestName;
    }

    const others = selectedPeople.length - 1;
    return tq.plural(others, {
      one: tq`${firstGuestName} and 1 other guest`,
      other: tq`${firstGuestName} and ${others} other guests`,
    });
  }, [selectedPeople, tq]);

  return (
    <StyledBox
      display={['flex', 'flex', 'none']}
      paddingX="16px"
      paddingY="8px"
      marginBottom="8px"
      $highlighted={isHighlighted}
      $shadowOpacity={opacity * 0.3}
    >
      <Box
        color={'mono10'}
        marginRight={[4, 4, 5]}
      >{tq`To: `}</Box>
      {isHighlighted ? (
        <GuestPickerButton
          itemType="withDeliveryMethod"
          enableSmsSelection={false}
          emailOnly
          initialDeliveryMethod="email"
          title={
            <TextV2
              typographyVariant="hed4"
              color="mono14"
              display="flex"
              alignItems="center"
              fontSize="24px"
              lineHeight="1"
            >
              <AddGuestsSvg style={{ height: 28, width: 28, marginRight: 12 }} /> {tq`Select Guests`}
            </TextV2>
          }
          buttonTextDefault={text}
          buttonTextHover={text}
          variant="ghost"
          padding={0}
          height="auto"
          typographyVariant="body1"
          fontWeight={500}
          minHeight="auto"
          lineHeight={pxToRem(19)}
          color="linkText"
          _hover={{ backgroundColor: 'transparent' }}
          _active={{ backgroundColor: 'transparent' }}
          useCase={{ area: 'emailsAndEcards', subArea: 'emailEditor' }}
        />
      ) : (
        <Box color="mono13">{text}</Box>
      )}
    </StyledBox>
  );
};

export default RecipientsBlock;
